import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal';
import { TargetingFields } from '@openx/types/targeting/targeting';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getContextualMaxPom } from './getContextualMaxPom';
import { getIncludedContextualSegments } from './getIncludedContextualSegments';
import { useAccountNetworkIdFetch } from './useAccountNetworkIdFetch';
import { useSelectedContextualSegmentsFetch } from './useSelectedContextualSegmentsFetch';

export const useSetContextualMaxPom = (): { isContextualFeePending: boolean } => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    setValue,
    trigger,
    getFieldState,
    formState: { isSubmitted },
  } = useFormContext<DealFormDataWithTargeting>();

  const accountUid = useWatch<DealFormDataWithTargeting, typeof dealFormField.ACCOUNT_UID>({
    name: dealFormField.ACCOUNT_UID,
  });
  const targeting = useWatch<DealFormDataWithTargeting, 'targeting.targeting'>({
    name: 'targeting.targeting',
  });
  const storedContextualMaxPom = useWatch<DealFormDataWithTargeting, typeof dealFormField.CONTEXTUAL_MAX_POM>({
    name: dealFormField.CONTEXTUAL_MAX_POM,
  });

  const contextual = targeting?.[TargetingFields.CONTEXTUAL_SEGMENTS];
  const includedSegments = useMemo(() => getIncludedContextualSegments(contextual), [contextual]);

  const hasIncludedSegments = includedSegments.segmentIds.length > 0;
  const {
    accountId,
    error: accountError,
    loading: accountLoading,
  } = useAccountNetworkIdFetch(hasIncludedSegments ? accountUid : undefined);

  const {
    segments,
    error: segmentsError,
    loading: segmentsLoading,
  } = useSelectedContextualSegmentsFetch({
    accountId: hasIncludedSegments ? accountId : undefined,
    providerIds: includedSegments.providerIds,
    segmentIds: includedSegments.segmentIds,
  });

  const loading = hasIncludedSegments && (accountLoading || segmentsLoading);
  // without the account id the prices cannot be fetched, so the contextual fee cannot be verified
  const hasLookupFailed = hasIncludedSegments && !loading && (!!accountError || !!segmentsError || !accountId);
  const contextualMaxPom = useMemo(
    () => (hasIncludedSegments ? getContextualMaxPom(segments, includedSegments) : null),
    [hasIncludedSegments, includedSegments, segments]
  );

  useEffect(() => {
    if (hasLookupFailed) {
      enqueueSnackbar(t('Contextual segment fees could not be loaded. Reload the page to try again.'), {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, hasLookupFailed, t]);

  // the stored value lags one render behind the computed one, until the effect below syncs it
  const isOutOfSync = (storedContextualMaxPom ?? null) !== contextualMaxPom;

  useEffect(() => {
    // compared against the form value (not a ref), so the field is resynced also after a form reset
    if (loading || hasLookupFailed || !isOutOfSync) return;

    setValue(dealFormField.CONTEXTUAL_MAX_POM, contextualMaxPom, { shouldDirty: false });

    // Re-validate the 94% limit only when the curated deal fee was already touched, to not show premature errors
    const grossShareField = 'third_party_fees_config.0.gross_share' as const;
    if (isSubmitted || getFieldState(grossShareField).isTouched) {
      trigger(grossShareField);
    }
  }, [contextualMaxPom, getFieldState, hasLookupFailed, isOutOfSync, isSubmitted, loading, setValue, trigger]);

  // a stale or missing contextual fee would let the 94% check pass, so the deal cannot be saved until it is current
  return { isContextualFeePending: loading || hasLookupFailed || isOutOfSync };
};
